import _                       from 'underscore';
import { useEffect, useState }      from 'react';
import { getHeight, getWidth, sum } from '../util';


const windowResizeEffectFactory: ( handler?: () => void ) => any = ( resizeHandler = () => {} ) => {

    // set resize listener
    window.addEventListener( 'resize', resizeHandler );

    // clean up function
    return () => {
        // remove resize listener
        window.removeEventListener( 'resize', resizeHandler );
    }
}

const useWindowResize: ( ms?: number ) => ( [ width: number, height: number, date: Date ] ) = ( debounceRateMS = 150 ) => {

    // save current window width in the state object
    let [ width, setWidth ] = useState<number>( getWidth() );
    let [ height, setHeight ] = useState<number>( getHeight() );

    // in this case useEffect will execute only once because
    // it does not have any dependencies.
    const resizeListener = _.debounce( () => {

        // change width from the state object
        setWidth( getWidth() );
        setHeight( getHeight() );

    }, debounceRateMS );

    useEffect( () => windowResizeEffectFactory( resizeListener ), [] )

    // include date since width / height may not have changed
    // but a resize event still occurs, and we should let components
    // decide if they want the state to update or not.
    return [ width, height, new Date() ];
}

interface UseWindowMeasurementsOptions {

    // A set of numbers representing height thresholds to consider for the height calculations
    // being used in conjunction with maxDialogHeightModifier, maxDialogHeight;
    // Basically, a ad-hoc media query.
    maxDialogHeightThreshold?: Partial<{ lg: number, sm: number }>,

    // A set of numbers between 0 and 1 to multiply the height by
    // to arrive at the max height a modal can be honored at,
    // lower values = smaller ratio'ed height calculations,
    // higher = larger height usage; to ignore and use fixed heights, se
    // these to null.
    maxDialogHeightModifier?: Partial<{ lg: number, sm: number | null }>,

    // A set of fixed numbers specifying the max height of each threshold (lg, sm, xs);
    // A fixed xs size is only used when the height of the screen is < maxDialogHeightModifier.sm
    maxDialogHeight?: Partial<{ lg: number | null, sm: number | null, xs: number | null, }>,
}

const defaultMeasurementOptions: UseWindowMeasurementsOptions = {
    maxDialogHeightThreshold: { lg: 960, sm: 768 },
    maxDialogHeightModifier:  { lg: 0.6, sm: null },
    maxDialogHeight:          { lg: null, sm: 320, xs: 200 },
};

const useWindowMeasurements = ( _options: UseWindowMeasurementsOptions = defaultMeasurementOptions ) => {

    let options = _.mapObject( _.clone( defaultMeasurementOptions ), ( optionDef, key ) => {
        let userValue      = _options[ key ] ?? null,
            newOptionValue = optionDef;

        return ( userValue )
               ? _.isObject( newOptionValue )
                 ? { ...newOptionValue, ...userValue }
                 : userValue
               : newOptionValue;
    } )

    let [ width, height ] = useWindowResize();

    // when window height changes..
    useEffect( () => {

        console.log( { height, maxDialogHeight } )

    }, [ height ] );

    let isLargeScreenHeight           = ( height > options.maxDialogHeightThreshold.lg ),
        isSmallScreenHeight           = ( height > options.maxDialogHeightThreshold.sm ),
        hasLargeMaxHeightSpecified    = ( !_.isNull( options.maxDialogHeight.lg ) ),
        hasSmallMaxHeightSpecified    = ( !_.isNull( options.maxDialogHeight.sm ) ),
        largeScreenProportionalHeight = ( height - Math.floor( height * options.maxDialogHeightModifier.lg ) ),
        smallScreenProportionalHeight = ( height - Math.floor( height * options.maxDialogHeightModifier.sm ) );

    let maxDialogHeight = isLargeScreenHeight
                          ? hasLargeMaxHeightSpecified
                            ? options.maxDialogHeight.lg
                            : largeScreenProportionalHeight
                          : isSmallScreenHeight
                            ? hasSmallMaxHeightSpecified
                              ? options.maxDialogHeight.sm
                              : smallScreenProportionalHeight
                            : options.maxDialogHeight.xs;

    const maxVisibleMainAreaHeight = () => {

        const mainLayout      = document.querySelector( '.layout--frame-main' ),
              mainTitleLayout = document.querySelector( '.layout--frame-page-title-container' );

        let maxVisibleHeight = 0;

        if( mainLayout ){

            maxVisibleHeight = mainLayout.getBoundingClientRect().height;

            if( mainTitleLayout ){

                maxVisibleHeight -= mainTitleLayout.getBoundingClientRect().height;
            }
        }

        return maxVisibleHeight;
    }

    let getSidebarTopOffset = (): number => {

        return getCollectiveElementHeight( [ 'layout--frame-top' ] );
    }

    let getSidebarBottomOffset = (): number => {

        return getCollectiveElementHeight( [ 'layout--frame-legal' ] );
    }

    let getTitleContainerHeight = (): number => {

        return getCollectiveElementHeight( [ 'layout--frame-page-title-container' ] );
    }

    let getFilterBarContainerHeight = (): number => {

        return getCollectiveElementHeight( [ 'layout--filter-bar' ] );
    }

    let getCollectiveElementHeight = ( elementList: string[] ): number => {

        let elementHeights = [] as number[];

        elementList.map( element => {

            /* Get element from page */
            let [ el ] = document.getElementsByClassName( element );

            /* Push height if available */
            if( el ){ elementHeights.push( el.getClientRects()[ 0 ].height ) }
        } )

        return sum( elementHeights );

    }

    return {
        maxDialogHeight,
        maxVisibleMainAreaHeight,
        getSidebarTopOffset,
        getSidebarBottomOffset,
        getTitleContainerHeight,
        getFilterBarContainerHeight,
        getCollectiveElementHeight,
    }
}

export {
    useWindowResize,
    windowResizeEffectFactory,
    useWindowMeasurements
}
